// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import dayjs from 'dayjs';
import timezoneDayJs from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { type RecurringOptions, createRecurrence } from './recurrence';
import {
  MIN_PLANTING_CREATION_BUFFER_SECONDS,
  PLANTING_CREATION_BUFFER_SECONDS,
  RECURRING_FREQUENCY_UNITS,
} from './types';
import { utils } from './utils';

dayjs.extend(utc);
dayjs.extend(timezoneDayJs);

export function getRecurringPlantingTimesToCreate({
  recurringStartTime,
  recurringEndTime,
  recurringOptions,
  timezone: timezoneIn,
  growTime,
  ignoreGrowTimeLimit = false,
}: {
  readonly recurringStartTime: number;
  readonly recurringEndTime?: number;
  readonly timezone: string;
  readonly recurringOptions: RecurringOptions;
  readonly growTime: number;
  readonly ignoreGrowTimeLimit?: boolean;
}): readonly number[] {
  // Use now + grow time because we would otherwise potentially create a planting that can't be started in time
  const todayStart = utils.todayStart(timezoneIn);

  const isWeeklyFrequencyType = recurringOptions.frequencyGroups[0].frequencyType === RECURRING_FREQUENCY_UNITS.WEEKS;

  // If weekly frequency then we need to start at the recurring start time (or on the same day of the week as expected)
  // TOOD: we should not use start time since it could have performance implications
  // But we also can't use the last task time since there are problems when start time is changed
  const startTimeToUse = isWeeklyFrequencyType
    ? recurringStartTime
    : // If we want to ignore the grow time limitation
      Math.max(recurringStartTime, ignoreGrowTimeLimit ? todayStart.unix() : todayStart.unix() + growTime);
  // Logic here needs to be the same as in NextPlantingsTable.tsx
  const endTimeToUse = Math.min(
    // Use || instead of ?? here in case recurringEndTime is NaN
    recurringEndTime || Number.POSITIVE_INFINITY,
    todayStart.add(12, 'hours').hour(12).unix() +
      // Create plantings at least 30 days in advance, unless the longest grow time + 1 week is greater than 30 days
      Math.max(growTime + PLANTING_CREATION_BUFFER_SECONDS, MIN_PLANTING_CREATION_BUFFER_SECONDS),
  );
  if (startTimeToUse > endTimeToUse) {
    return [];
  }
  const recurrence = createRecurrence({
    startTime: startTimeToUse,
    endTime: endTimeToUse,
    options: recurringOptions,
  });

  const times = recurrence
    .all()
    .map((date) => date.tz(timezoneIn, true))
    .map((date) => utils.toNoonTime(date, timezoneIn).unix());

  return times;
}
